import { useEffect, useRef, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  allStudentFeeReport,
  studentFeeReportBatchWise,
} from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { batchList } from "../../api/Batch";

const inr = (n) =>
  "₹ " +
  Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

/* ------------------------------------------------------------------ */
/*  EditableFeeCell                                                   */
/*  Excel-style inline editable cell:                                 */
/*   - click / Enter to edit                                          */
/*   - Enter / Tab / blur commits + autosaves                         */
/*   - Escape cancels and restores previous value                     */
/*   - shows a small spinner while saving, a check on success         */
/* ------------------------------------------------------------------ */
function EditableFeeCell({
  value,
  disabled,
  onCommit, // async (newValue) => boolean (true = success)
  onTabNext, // called after a successful commit via Tab, to focus next cell
  registerRef, // (el) => void, lets the parent grid focus this cell programmatically
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (disabled) return;
    setDraft(value);
    setEditing(true);
  };

  const commit = async (moveNext) => {
    const newValue = Number(draft);
    setEditing(false);

    if (Number.isNaN(newValue) || newValue === Number(value)) {
      setStatus("idle");
      if (moveNext) onTabNext?.();
      return;
    }

    setStatus("saving");
    const ok = await onCommit(newValue);
    if (ok) {
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 1000);
    } else {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 1500);
    }
    if (moveNext) onTabNext?.();
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  return (
    <td
      ref={(el) => {
        wrapRef.current = el;
        registerRef?.(el);
      }}
      className="text-end p-0"
      style={{ position: "relative" }}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          className="form-control form-control-sm text-end border-primary"
          style={{ borderRadius: 0 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(false);
            } else if (e.key === "Tab") {
              e.preventDefault();
              commit(true);
            } else if (e.key === "Escape") {
              cancel();
            }
          }}
        />
      ) : (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") startEdit();
          }}
          className="px-2 py-2 d-flex align-items-center justify-content-end gap-1"
          style={{
            cursor: disabled ? "default" : "text",
            minHeight: 38,
          }}
          title={disabled ? "" : "Click to edit"}
        >
          <span>{inr(value)}</span>
          {status === "saving" && (
            <i className="ri-loader-4-line spin" style={{ fontSize: 12 }}></i>
          )}
          {status === "saved" && (
            <i
              className="ri-check-line text-success"
              style={{ fontSize: 12 }}
            ></i>
          )}
          {status === "error" && (
            <i
              className="ri-error-warning-line text-danger"
              style={{ fontSize: 12 }}
            ></i>
          )}
        </div>
      )}
    </td>
  );
}

export default function BatchFeesEdit(route) {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({});
  const [batchWiseData, setBatchWiseData] = useState({});
  const [BatchStudents, setBatchStudents] = useState({});
  const [batches, setBatches] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const batch_id = searchParams.get("batch_id") || "";
  const batchId = searchParams.get("batch_id") || "";
  // const [batchId, setBatchId] = useState(batch_id || "");

  // grid cell refs, used to move focus on Tab: `${studentId}-${field}`
  const cellRefs = useRef({});
  const editableFields = [
    "batch",
    "hostel",
    "library",
    "transport",
    "discount",
  ];

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const buildQuery = () => {
    let addUrl = "with=feetype&batch_id=" + batch_id + "&id=" + batch_id;
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
    });
    return addUrl;
  };

  const getStudentFeeReportBatchWise = async () => {
    setLoading(true);
    try {
      const response = await studentFeeReportBatchWise(buildQuery());
      if (response.data.status) {
        setBatchWiseData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatches = async () => {
    setLoading(true);
    try {
      const response = await batchList(buildQuery());
      if (response.data.status) {
        setBatches(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };
  const getStudentsofBatch = async () => {
    setLoading(true);
    try {
      const response = await allStudentFeeReport(buildQuery());
      if (response.data.status) {
        setBatchStudents(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatches();
    getStudentFeeReportBatchWise();
    getStudentsofBatch();
  }, [filters]);

  /* ------------------------------------------------------------------ */
  /*  Inline fee editing                                                */
  /* ------------------------------------------------------------------ */

  // maps our short field keys to which fee-array + which record field to touch
  const FIELD_MAP = {
    batch: {
      arrayKey: "student_batch_n_fees",
      recordField: "final_fee",
      feeType: "batch",
    },
    discount: {
      arrayKey: "student_batch_n_fees",
      recordField: "total_discount",
      feeType: "batch_discount",
    },
    hostel: {
      arrayKey: "student_hostel_n_fees",
      recordField: "final_amount",
      feeType: "hostel",
    },
    library: {
      arrayKey: "student_library_n_fees",
      recordField: "final_amount",
      feeType: "library",
    },
    transport: {
      arrayKey: "student_transport_n_fees",
      recordField: "final_amount",
      feeType: "transport",
    },
  };

  const getFeeRecord = (student, field) => {
    const { arrayKey } = FIELD_MAP[field];
    return student?.[arrayKey]?.[0] || null;
  };

  const getFieldValue = (student, field) => {
    const { recordField } = FIELD_MAP[field];
    const record = getFeeRecord(student, field);
    return Number(record?.[recordField] || 0);
  };

  const handleCellCommit = async (studentId, field, newValue) => {
    const students = Array.isArray(BatchStudents) ? BatchStudents : [];
    const studentIndex = students.findIndex((s) => s.id === studentId);
    if (studentIndex === -1) return false;

    const student = students[studentIndex];
    const { arrayKey, recordField, feeType } = FIELD_MAP[field];
    const record = getFeeRecord(student, field);

    if (!record) {
      SweetAlertShow(
        "info",
        "Warning",
        "No fee record found for this student to update.",
      );
      return false;
    }

    const previousValue = Number(record[recordField] || 0);

    // optimistic update
    const updatedStudents = students.map((s, idx) => {
      if (idx !== studentIndex) return s;
      const updatedArray = (s[arrayKey] || []).map((rec, i) =>
        i === 0 ? { ...rec, [recordField]: newValue } : rec,
      );
      return { ...s, [arrayKey]: updatedArray };
    });
    setBatchStudents(updatedStudents);

    try {
      const response = "";
      // const response = await updateStudentFeeAmount({
      //   student_id: studentId,
      //   fee_id: record.id,
      //   fee_type: feeType,
      //   field: recordField,
      //   value: newValue,
      // });

      if (!response?.data?.status) {
        throw new Error(response?.data?.message || "Save failed");
      }
      return true;
    } catch (error) {
      // rollback on failure
      const rolledBack = students.map((s, idx) => {
        if (idx !== studentIndex) return s;
        const revertedArray = (s[arrayKey] || []).map((rec, i) =>
          i === 0 ? { ...rec, [recordField]: previousValue } : rec,
        );
        return { ...s, [arrayKey]: revertedArray };
      });
      setBatchStudents(rolledBack);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Could not save changes",
      );
      return false;
    }
  };

  const focusNextCell = (studentId, field) => {
    const students = Array.isArray(BatchStudents) ? BatchStudents : [];
    const rowIndex = students.findIndex((s) => s.id === studentId);
    const fieldIndex = editableFields.indexOf(field);

    let nextRowIndex = rowIndex;
    let nextFieldIndex = fieldIndex + 1;

    if (nextFieldIndex >= editableFields.length) {
      nextFieldIndex = 0;
      nextRowIndex = rowIndex + 1;
    }

    const nextStudent = students[nextRowIndex];
    if (!nextStudent) return;

    const nextField = editableFields[nextFieldIndex];
    const key = `${nextStudent.id}-${nextField}`;
    const el = cellRefs.current[key];
    if (el) {
      const clickable = el.querySelector('[role="button"]');
      clickable?.click();
    }
  };

  const inr2 = inr; // alias kept for parity with rest of file

  const handleDownload = async (type = "excel") => {
    setLoading(true);

    try {
      const students = Array.isArray(BatchStudents) ? BatchStudents : [];

      if (!students.length) {
        SweetAlertShow("info", "Info", "No records found");
        return;
      }

      const downloadData = students.map((student, index) => {
        // -----------------------------
        // Batch Fee
        // -----------------------------
        const batchFees = student.student_batch_n_fees || [];

        const batchFee = batchFees.reduce(
          (total, fee) => total + Number(fee.final_fee || 0),
          0,
        );

        const batchDiscount = batchFees.reduce(
          (total, fee) => total + Number(fee.total_discount || 0),
          0,
        );

        const batchPaid = batchFees.reduce(
          (total, fee) => total + Number(fee.total_paid || 0),
          0,
        );

        const batchDue = batchFees.reduce(
          (total, fee) => total + Number(fee.total_due || 0),
          0,
        );

        // -----------------------------
        // Hostel Fee
        // -----------------------------
        const hostelFees = student.student_hostel_n_fees || [];

        const hostelFee = hostelFees.reduce(
          (total, fee) => total + Number(fee.final_amount || 0),
          0,
        );

        const hostelPaid = hostelFees.reduce(
          (total, fee) => total + Number(fee.paid_amount || 0),
          0,
        );

        const hostelDue = hostelFees.reduce(
          (total, fee) => total + Number(fee.left_amount || 0),
          0,
        );

        // -----------------------------
        // Library Fee
        // -----------------------------
        const libraryFees = student.student_library_n_fees || [];

        const libraryFee = libraryFees.reduce(
          (total, fee) => total + Number(fee.final_amount || 0),
          0,
        );

        const libraryPaid = libraryFees.reduce(
          (total, fee) => total + Number(fee.paid_amount || 0),
          0,
        );

        const libraryDue = libraryFees.reduce(
          (total, fee) => total + Number(fee.left_amount || 0),
          0,
        );

        // -----------------------------
        // Transport Fee
        // -----------------------------
        const transportFees = student.student_transport_n_fees || [];

        const transportFee = transportFees.reduce(
          (total, fee) => total + Number(fee.final_amount || 0),
          0,
        );

        const transportPaid = transportFees.reduce(
          (total, fee) => total + Number(fee.paid_amount || 0),
          0,
        );

        const transportDue = transportFees.reduce(
          (total, fee) => total + Number(fee.left_amount || 0),
          0,
        );

        // -----------------------------
        // Overall Totals
        // -----------------------------
        const totalPayable = batchFee + hostelFee + libraryFee + transportFee;

        const totalPaid = batchPaid + hostelPaid + libraryPaid + transportPaid;

        const totalDue = batchDue + hostelDue + libraryDue + transportDue;

        return {
          "#": index + 1,
          "Registration No": student.registration_no,
          Student: student.name,
          "Father Name": student.father_name,

          "Batch Fee": batchFee,
          Hostel: hostelFee,
          Library: libraryFee,
          Transport: transportFee,

          Discount: batchDiscount,

          "Payable Fee": totalPayable,
          "Received Fee": totalPaid,
          Due: totalDue,

          Status: totalDue > 0 ? "Due" : "Paid",
        };
      });

      // -----------------------------
      // TOTAL ROW
      // -----------------------------
      const totalRow = {
        "#": "",
        "Registration No": "",
        Student: "TOTAL",
        "Father Name": "",

        "Batch Fee": downloadData.reduce(
          (t, r) => t + Number(r["Batch Fee"] || 0),
          0,
        ),

        Hostel: downloadData.reduce((t, r) => t + Number(r.Hostel || 0), 0),

        Library: downloadData.reduce((t, r) => t + Number(r.Library || 0), 0),

        Transport: downloadData.reduce(
          (t, r) => t + Number(r.Transport || 0),
          0,
        ),

        Discount: downloadData.reduce((t, r) => t + Number(r.Discount || 0), 0),

        "Payable Fee": downloadData.reduce(
          (t, r) => t + Number(r["Payable Fee"] || 0),
          0,
        ),

        "Received Fee": downloadData.reduce(
          (t, r) => t + Number(r["Received Fee"] || 0),
          0,
        ),

        Due: downloadData.reduce((t, r) => t + Number(r.Due || 0), 0),

        Status: "",
      };

      downloadData.push(totalRow);

      // -----------------------------
      // Download / Print
      // -----------------------------
      const now = new Date();

      const fileName = `Batch_Fee_Report_${now.getFullYear()}_${
        now.getMonth() + 1
      }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

      if (type === "excel") {
        DownloadExcel(downloadData, [], fileName, []);
      } else {
        PrintPage(downloadData);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {loading && <LoadingDiv />}
      <style>{`
        .spin { animation: fee-cell-spin 0.8s linear infinite; display: inline-block; }
        @keyframes fee-cell-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    {/* Header / Filters */}
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-money-rupee-circle-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">
                              Batch Wise Students Fees Report / Dashboard
                            </h5>
                            <small className="text-muted">
                              Monitor fees collection and analytics
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "220px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  session_id: e.target.value,
                                })
                              }
                            >
                              {sessions?.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session} :{" "}
                                  {s.status === "1" ? "Active" : "Inactive"}
                                </option>
                              ))}
                            </select>
                            <label>Session</label>
                          </div>

                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "220px" }}
                          >
                            {/* {JSON.stringify(batches)} */}
                            <select
                              className="form-select rounded-3"
                              value={batchId}
                              onChange={(e) => {
                                const selectedBatchId = e.target.value;

                                setSearchParams({
                                  batch_id: selectedBatchId,
                                });

                                setFilters((prev) => ({
                                  ...prev,
                                  batch_id: selectedBatchId,
                                }));
                              }}
                            >
                              <option value="">Select Batch</option>
                              {batches?.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.title}
                                </option>
                              ))}
                            </select>
                            <label>Batch</label>
                          </div>

                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "180px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.rte_status || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  rte_status: e.target.value,
                                })
                              }
                            >
                              <option value="">All</option>
                              <option value="RTE">RTE</option>
                              <option value="NON-RTE">NON-RTE</option>
                            </select>
                            <label>RTE Status</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Batch-wise Report */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      {/* Header */}
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #10b981, #22c55e)",
                            }}
                          >
                            <i className="ri-stack-line fs-5"></i>
                          </div>

                          <div>
                            <h6 className="m-0 fw-bold">
                              Batch Wise Students Fee Report
                            </h6>
                            <small className="text-muted">
                              Fees summary grouped by batch Students &middot;
                              click any amount to edit
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            {batchWiseData?.batch_wise?.length || 0} Batches
                          </span>

                          <button
                            type="button"
                            className="btn btn-outline-primary rounded-3"
                            onClick={() => handleDownload("excel")}
                          >
                            <i className="ri-file-excel-2-line me-2"></i>{" "}
                            Download
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-info rounded-3"
                            onClick={() => handleDownload("print")}
                          >
                            <i className="ri-printer-line me-2"></i> Print
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 table-bordered">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4">#</th>
                              <th>Student</th>
                              <th className="text-end">Batch Fee</th>
                              <th className="text-end">Hostel</th>
                              <th className="text-end">Library</th>
                              <th className="text-end">Transport</th>
                              <th className="text-end">Discount</th>
                              <th className="text-end">Payable</th>
                              <th className="text-end">Paid</th>
                              <th className="text-end">Due</th>
                              <th className="text-center pe-4">Status</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!BatchStudents?.length ? (
                              <tr>
                                <td
                                  colSpan="11"
                                  className="text-center py-5 text-muted"
                                >
                                  No students available
                                </td>
                              </tr>
                            ) : (
                              BatchStudents.map((student, i) => {
                                const batch_total_fee =
                                  student.student_batch_n_fees?.reduce(
                                    (total, fee) =>
                                      total + Number(fee.total_fee || 0),
                                    0,
                                  ) || 0;

                                const batchFee = getFieldValue(
                                  student,
                                  "batch",
                                );
                                const batchDiscount = getFieldValue(
                                  student,
                                  "discount",
                                );

                                const batchPaid =
                                  student.student_batch_n_fees?.reduce(
                                    (total, fee) =>
                                      total + Number(fee.total_paid || 0),
                                    0,
                                  ) || 0;

                                const hostelFee = getFieldValue(
                                  student,
                                  "hostel",
                                );
                                const hostelPaid =
                                  student.student_hostel_n_fees?.reduce(
                                    (total, fee) =>
                                      total + Number(fee.paid_amount || 0),
                                    0,
                                  ) || 0;

                                const libraryFee = getFieldValue(
                                  student,
                                  "library",
                                );
                                const libraryPaid =
                                  student.student_library_n_fees?.reduce(
                                    (total, fee) =>
                                      total + Number(fee.paid_amount || 0),
                                    0,
                                  ) || 0;

                                const transportFee = getFieldValue(
                                  student,
                                  "transport",
                                );
                                const transportPaid =
                                  student.student_transport_n_fees?.reduce(
                                    (total, fee) =>
                                      total + Number(fee.paid_amount || 0),
                                    0,
                                  ) || 0;

                                const totalPayable =
                                  batchFee -
                                  batchDiscount +
                                  hostelFee +
                                  libraryFee +
                                  transportFee;

                                const totalPaid =
                                  batchPaid +
                                  hostelPaid +
                                  libraryPaid +
                                  transportPaid;

                                const totalDue = Math.max(
                                  totalPayable - totalPaid,
                                  0,
                                );

                                const feeStatus = totalDue > 0 ? "Due" : "Paid";

                                return (
                                  <tr key={student.id}>
                                    {/* # */}
                                    <td className="ps-4 fw-semibold">
                                      {i + 1}
                                    </td>

                                    {/* Student */}
                                    <td>
                                      <span className="badge bg-light text-dark border">
                                        {student.registration_no}
                                      </span>
                                      {student.student_batch_n_fees[0]
                                        ?.rte_status == "RTE" && (
                                        <span className="badge bg-success text-dark border">
                                          {
                                            student.student_batch_n_fees[0]
                                              .rte_status
                                          }
                                        </span>
                                      )}
                                      <div className="fw-semibold">
                                        {student.name}
                                      </div>

                                      {student.father_name && (
                                        <small className="text-muted">
                                          S/O {student.father_name}
                                        </small>
                                      )}
                                    </td>

                                    {/* Batch Fee - editable */}
                                    <EditableFeeCell
                                      value={batchFee}
                                      disabled={!getFeeRecord(student, "batch")}
                                      onCommit={(val) =>
                                        handleCellCommit(
                                          student.id,
                                          "batch",
                                          val,
                                        )
                                      }
                                      onTabNext={() =>
                                        focusNextCell(student.id, "batch")
                                      }
                                      registerRef={(el) =>
                                        (cellRefs.current[
                                          `${student.id}-batch`
                                        ] = el)
                                      }
                                    />

                                    {/* Hostel - editable */}
                                    <EditableFeeCell
                                      value={hostelFee}
                                      disabled={
                                        !getFeeRecord(student, "hostel")
                                      }
                                      onCommit={(val) =>
                                        handleCellCommit(
                                          student.id,
                                          "hostel",
                                          val,
                                        )
                                      }
                                      onTabNext={() =>
                                        focusNextCell(student.id, "hostel")
                                      }
                                      registerRef={(el) =>
                                        (cellRefs.current[
                                          `${student.id}-hostel`
                                        ] = el)
                                      }
                                    />

                                    {/* Library - editable */}
                                    <EditableFeeCell
                                      value={libraryFee}
                                      disabled={
                                        !getFeeRecord(student, "library")
                                      }
                                      onCommit={(val) =>
                                        handleCellCommit(
                                          student.id,
                                          "library",
                                          val,
                                        )
                                      }
                                      onTabNext={() =>
                                        focusNextCell(student.id, "library")
                                      }
                                      registerRef={(el) =>
                                        (cellRefs.current[
                                          `${student.id}-library`
                                        ] = el)
                                      }
                                    />

                                    {/* Transport - editable */}
                                    <EditableFeeCell
                                      value={transportFee}
                                      disabled={
                                        !getFeeRecord(student, "transport")
                                      }
                                      onCommit={(val) =>
                                        handleCellCommit(
                                          student.id,
                                          "transport",
                                          val,
                                        )
                                      }
                                      onTabNext={() =>
                                        focusNextCell(student.id, "transport")
                                      }
                                      registerRef={(el) =>
                                        (cellRefs.current[
                                          `${student.id}-transport`
                                        ] = el)
                                      }
                                    />

                                    {/* Discount - editable */}
                                    <EditableFeeCell
                                      value={batchDiscount}
                                      disabled={
                                        !getFeeRecord(student, "discount")
                                      }
                                      onCommit={(val) =>
                                        handleCellCommit(
                                          student.id,
                                          "discount",
                                          val,
                                        )
                                      }
                                      onTabNext={() =>
                                        focusNextCell(student.id, "discount")
                                      }
                                      registerRef={(el) =>
                                        (cellRefs.current[
                                          `${student.id}-discount`
                                        ] = el)
                                      }
                                    />

                                    {/* Payable - computed, read only */}
                                    <td className="text-end fw-semibold">
                                      {inr(totalPayable)}
                                    </td>

                                    {/* Paid - read only (comes from payments) */}
                                    <td className="text-end text-success fw-semibold">
                                      {inr(totalPaid)}
                                    </td>

                                    {/* Due - computed, read only */}
                                    <td className="text-end text-warning fw-semibold">
                                      {inr(totalDue)}
                                    </td>

                                    {/* Status */}
                                    <td className="text-center">
                                      {feeStatus === "Paid" ? (
                                        <span className="badge bg-success-subtle text-success">
                                          Paid
                                        </span>
                                      ) : (
                                        <span className="badge bg-warning-subtle text-warning">
                                          Due
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>

                          {/* TOTAL */}
                          {!!BatchStudents?.length && (
                            <tfoot>
                              {(() => {
                                const totals = BatchStudents.reduce(
                                  (acc, student) => {
                                    const batchFee = getFieldValue(
                                      student,
                                      "batch",
                                    );
                                    const discount = getFieldValue(
                                      student,
                                      "discount",
                                    );
                                    const hostel = getFieldValue(
                                      student,
                                      "hostel",
                                    );
                                    const library = getFieldValue(
                                      student,
                                      "library",
                                    );
                                    const transport = getFieldValue(
                                      student,
                                      "transport",
                                    );

                                    const batchFees =
                                      student.student_batch_n_fees || [];
                                    const hostelFees =
                                      student.student_hostel_n_fees || [];
                                    const libraryFees =
                                      student.student_library_n_fees || [];
                                    const transportFees =
                                      student.student_transport_n_fees || [];

                                    acc.batchFee += batchFee;
                                    acc.discount += discount;
                                    acc.hostel += hostel;
                                    acc.library += library;
                                    acc.transport += transport;

                                    acc.paid +=
                                      batchFees.reduce(
                                        (t, f) => t + Number(f.total_paid || 0),
                                        0,
                                      ) +
                                      hostelFees.reduce(
                                        (t, f) =>
                                          t + Number(f.paid_amount || 0),
                                        0,
                                      ) +
                                      libraryFees.reduce(
                                        (t, f) =>
                                          t + Number(f.paid_amount || 0),
                                        0,
                                      ) +
                                      transportFees.reduce(
                                        (t, f) =>
                                          t + Number(f.paid_amount || 0),
                                        0,
                                      );

                                    return acc;
                                  },
                                  {
                                    batchFee: 0,
                                    hostel: 0,
                                    library: 0,
                                    transport: 0,
                                    discount: 0,
                                    paid: 0,
                                  },
                                );

                                const totalPayable =
                                  totals.batchFee -
                                  totals.discount +
                                  totals.hostel +
                                  totals.library +
                                  totals.transport;

                                const totalDue = Math.max(
                                  totalPayable - totals.paid,
                                  0,
                                );

                                return (
                                  <tr className="table-light fw-bold">
                                    <td className="ps-4"></td>

                                    <td>
                                      Total {BatchStudents.length} Students
                                    </td>

                                    <td className="text-end">
                                      {inr(totals.batchFee)}
                                    </td>

                                    <td className="text-end">
                                      {inr(totals.hostel)}
                                    </td>

                                    <td className="text-end">
                                      {inr(totals.library)}
                                    </td>

                                    <td className="text-end">
                                      {inr(totals.transport)}
                                    </td>

                                    <td className="text-end text-danger">
                                      {inr(totals.discount)}
                                    </td>

                                    <td className="text-end">
                                      {inr(totalPayable)}
                                    </td>

                                    <td className="text-end text-success">
                                      {inr(totals.paid)}
                                    </td>

                                    <td className="text-end text-warning">
                                      {inr(totalDue)}
                                    </td>

                                    <td></td>
                                  </tr>
                                );
                              })()}
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  BACKEND NOTE                                                      */
/* ------------------------------------------------------------------ */
/*
This file calls `updateStudentFeeAmount(payload)` from ../../api/Student,
which does not exist in your current api/Student.js yet. Add it there,
following whatever axios instance the rest of that file already uses
(replace `Api` below with your real import):

  export const updateStudentFeeAmount = (data) =>
    Api.post("student-fee/update-amount", data);

Payload sent on every save:
  {
    student_id,   // student.id
    fee_id,       // the specific fee record's id (student_batch_n_fees[0].id, etc.)
    fee_type,     // "batch" | "batch_discount" | "hostel" | "library" | "transport"
    field,        // the column being changed, e.g. "final_fee", "total_discount",
                  //  "final_amount"
    value,        // the new numeric amount
  }

On the Laravel side this maps to one endpoint that:
  1. looks up the fee row by fee_id + fee_type,
  2. validates the new value (e.g. can't be negative, can't push due below 0
     if you enforce that),
  3. updates `field`,
  4. recalculates total_due / left_amount for that row if you store it
     denormalized,
  5. returns { status: true, data: <updated row> } or
     { status: false, message: "..." } on validation failure.

If a student can have MORE THAN ONE active fee record per type, this
component currently always edits index [0] (same assumption the original
code already made for rte_status). Tell me if that's not safe for your
data and I'll add a record picker instead.
*/
